
/**
 * Attempts to trigger a media permission prompt if the current permission state is 'prompt'.
 * This is a "fire and forget" utility. It does not return a meaningful value for the caller
 * and does not dispatch Redux actions.
 * It relies on a separate mechanism (e.g., a Redux thunk monitoring PermissionStatus.onchange)
 * to update the application's state regarding the actual permission status.
 *
 * @param {'camera' | 'microphone'} mediaType - The type of media to request permission for.
 */
export async function mediaPermissionPrompt(mediaType) {
  let permissionName;
  let constraints;

  if (mediaType === 'camera') {
    permissionName = 'camera';
    constraints = { video: true };
  } else if (mediaType === 'microphone') {
    permissionName = 'microphone';
    constraints = { audio: true };
  } else {
    console.error('[PermissionPromptUtil] Invalid mediaType provided:', mediaType);
    return; // Exit if mediaType is incorrect
  }

  if (!navigator.permissions || !navigator.mediaDevices) {
    console.error('[PermissionPromptUtil] Essential browser APIs (Permissions or MediaDevices) are not supported.');
    // Your `monitorPermissionStatus` thunk should ideally reflect an 'unsupported'
    // state in Redux if it encounters this during its own checks.
    return;
  }

  try {
    const currentPermissionStatus = await navigator.permissions.query({ name: permissionName });

    // If permission is already granted or denied, there's nothing for this function to do
    // as navigator.mediaDevices.getUserMedia() would not typically show a prompt.
    // The existing state in Redux (managed by your monitor) should be correct.
    if (currentPermissionStatus.state === 'granted') {
      console.log(`[PermissionPromptUtil] Permission for ${permissionName} is already 'granted'. No prompt needed.`);
      return;
    }

    if (currentPermissionStatus.state === 'denied') {
      console.warn(`[PermissionPromptUtil] Permission for ${permissionName} is 'denied'. User must change this in browser settings. No prompt will be shown.`);
      // Your UI, reacting to the 'denied' state from Redux, should guide the user.
      return;
    }

    // Only proceed to call getUserMedia if the state is 'prompt'.
    if (currentPermissionStatus.state === 'prompt') {
      console.log(`[PermissionPromptUtil] Permission for ${permissionName} is 'prompt'. Attempting to trigger prompt via getUserMedia...`);
      try {
        // This is the call that will trigger the browser's permission prompt.
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // If getUserMedia resolves, the user has likely granted permission.
        // The 'monitorPermissionStatus' thunk's 'onchange' listener is responsible
        // for detecting the state change to 'granted' and updating Redux.
        console.log(`[PermissionPromptUtil] getUserMedia call for ${permissionName} was successful. Tracks obtained.`);

        // IMPORTANT: Immediately stop the tracks from this temporary stream
        // to release the hardware (camera/mic light off) and avoid resource leaks.
        stream.getTracks().forEach(track => {
          track.stop();
        });
        console.log(`[PermissionPromptUtil] Tracks from temporary ${permissionName} stream stopped.`);

      } catch (getUserMediaError) {
        // This catch block handles errors from navigator.mediaDevices.getUserMedia().
        // This includes the user explicitly denying the permission in the prompt (NotAllowedError),
        // or other errors like no device found (NotFoundError), hardware issues, etc.
        console.error(`[PermissionPromptUtil] Error during getUserMedia call for ${permissionName}:`, getUserMediaError.name, getUserMediaError.message);
        // If the user denied permission, the state will likely change to 'denied'.
        // If another error occurred, the state might remain 'prompt' or change to 'denied'.
        // In all these cases, your 'monitorPermissionStatus' thunk's 'onchange' listener
        // should pick up the resultant state and update Redux.
      }
    }
  } catch (queryError) {
    // This catches errors from navigator.permissions.query() itself.
    console.error(`[PermissionPromptUtil] Error querying permission status for ${permissionName}:`, queryError);
    // Your `monitorPermissionStatus` thunk might also encounter this and set an 'error'
    // state in Redux.
  }
}

export function getStoredDevice(key){
  try {
    const stored =localStorage.getItem(key);
    if(!stored) return null;
    return JSON.parse(stored);

  }catch (error) {
    console.warn(error);
  }
}